import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { LocalDateTime } from 'js-joda';
import { Warning as WarningIcon } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import DashboardDataTable from 'modules/common/dashboardDataTable.component';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';

const useStyles = makeStyles((theme) => ({
  filtersContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  formControl: {
    minWidth: 200,
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  noExceptions: {
    padding: theme.spacing(3),
    textAlign: 'center',
  },
  clickableLink: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
    textDecoration: 'underline',
    '&:hover': {
      textDecoration: 'none',
    },
  },
  descriptionCell: {
    maxWidth: 350,
    cursor: 'pointer',
  },
  statusCellContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  warningIcon: {
    fontSize: '1.2rem',
    color: theme.palette.warning.main,
  },
  // Status badge colors
  statusPending: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.common.white,
  },
  statusApproved: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.common.white,
  },
  statusDenied: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
  },
  statusOther: {
    backgroundColor: theme.palette.grey[500],
    color: theme.palette.common.white,
  },
}));

const ExceptionCommentsGrid = ({ exceptions, isLoading, clients, showExpenseAnalyst }) => {
  const classes = useStyles();

  // Filter state (moved internally)
  const [filters, setFilters] = useState({
    status: null,
    dateRange: null,
    clientId: null,
  });

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const dt = LocalDateTime.parse(dateString);
      const month = dt.month().toString().substring(0, 3);
      const day = dt.dayOfMonth();
      const year = dt.year();
      return `${month} ${day}, ${year}`;
    } catch (error) {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (amount == null) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const truncateText = (text, maxLength = 80) => {
    if (!text) return '-';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const getStatusBadgeClass = (status) => {
    if (!status) return classes.statusOther;
    const statusLower = status.toLowerCase();
    if (statusLower === 'pending') return classes.statusPending;
    if (statusLower === 'approved') return classes.statusApproved;
    if (statusLower === 'denied') return classes.statusDenied;
    return classes.statusOther;
  };

  const isPendingOlderThan7Days = (status, createdDate) => {
    if (!status || status.toLowerCase() !== 'pending' || !createdDate) return false;
    try {
      const created = new Date(createdDate);
      const now = new Date();
      const diffDays = Math.floor((now - created) / (1000 * 60 * 60 * 24));
      return diffDays > 7;
    } catch (error) {
      return false;
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      status: null,
      dateRange: null,
      clientId: null,
    });
  };

  // Extract unique statuses from exceptions
  const statuses = useMemo(() => {
    const uniqueStatuses = [...new Set(exceptions.map(exc => exc.status))].filter(Boolean);
    return uniqueStatuses.sort();
  }, [exceptions]);

  // Filter exceptions (sorting is handled by the grid)
  const filteredExceptions = useMemo(() => {
    let filtered = [...exceptions];

    // Apply status filter
    if (filters.status) {
      filtered = filtered.filter((exc) => exc.status?.toLowerCase() === filters.status.toLowerCase());
    }

    // TODO: Client filter disabled - API doesn't return clientId in exception response
    // Re-enable when backend adds clientId field to exception DTOs
    if (filters.clientId) {
      // Note: API doesn't return clientId, so we can't filter by it
      // This would need to be done server-side or we need clientId in response
    }

    // Apply date range filter
    if (filters.dateRange) {
      const now = new Date();
      const threshold = new Date();

      if (filters.dateRange === '7') {
        threshold.setDate(now.getDate() - 7);
      } else if (filters.dateRange === '30') {
        threshold.setDate(now.getDate() - 30);
      }

      if (filters.dateRange === '7' || filters.dateRange === '30') {
        filtered = filtered.filter((exc) => {
          const excDate = new Date(exc.createdDate);
          return excDate >= threshold;
        });
      }
    }

    return filtered;
  }, [exceptions, filters]);

  const columns = [
    {
      accessorKey: 'createdDate',
      header: 'Created Date',
      Cell: ({ cell }) => formatDate(cell.getValue()),
    },
    {
      accessorKey: 'authId',
      header: 'Auth ID',
      Cell: ({ row }) => (
        <a
          href={`/authorizations/${row.original.fileId}`}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.clickableLink}
        >
          {row.original.authId || '-'}
        </a>
      ),
    },
    {
      accessorKey: 'eeName',
      header: 'EE Name',
      Cell: ({ cell }) => cell.getValue() || '-',
    },
    // Only shown on the expense manager dashboard; EA dashboard rows have no analyst values
    ...(showExpenseAnalyst
      ? [
        {
          accessorKey: 'expenseAnalystName',
          header: 'Expense Analyst',
          Cell: ({ cell }) => cell.getValue() || '-',
        },
      ]
      : []),
    {
      accessorKey: 'clientName',
      header: 'Client Name',
      Cell: ({ cell }) => cell.getValue() || '-',
    },
    {
      accessorKey: 'exceptionDescription',
      header: 'Description',
      enableSorting: false,
      muiTableBodyCellProps: { className: classes.descriptionCell },
      Cell: ({ cell }) => (
        <Tooltip title={cell.getValue() || ''} arrow>
          <span>{truncateText(cell.getValue(), 80)}</span>
        </Tooltip>
      ),
    },
    {
      accessorKey: 'exceptionAmount',
      header: 'Amount',
      Cell: ({ cell }) => formatCurrency(cell.getValue()),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      Cell: ({ row }) => (
        <div className={classes.statusCellContainer}>
          {isPendingOlderThan7Days(row.original.status, row.original.createdDate) && (
            <Tooltip title="Pending for more than 7 days" arrow>
              <WarningIcon className={classes.warningIcon} />
            </Tooltip>
          )}
          <Chip
            label={row.original.status || 'Unknown'}
            size="small"
            className={getStatusBadgeClass(row.original.status)}
          />
        </div>
      ),
    },
    {
      accessorKey: 'createdByName',
      header: 'Created By',
      Cell: ({ cell }) => cell.getValue() || '-',
    },
  ];

  if (isLoading) {
    return (
      <Paper elevation={0} className={classes.tableContainer}>
        <Typography className={classes.noExceptions} color="textSecondary">
          Loading exceptions...
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      {/* Filter UI */}
      <Box className={classes.filtersContainer}>
        {/* Status Filter */}
        <FormControl className={classes.formControl} size="small" disabled={isLoading}>
          <InputLabel id="exception-status-filter-label">Status</InputLabel>
          <Select
            labelId="exception-status-filter-label"
            id="exception-status-filter"
            value={filters.status || ''}
            onChange={(e) => handleFilterChange('status', e.target.value || null)}
          >
            <MenuItem value="">
              <em>All Statuses</em>
            </MenuItem>
            {statuses.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Date Range Filter */}
        <FormControl className={classes.formControl} size="small" disabled={isLoading}>
          <InputLabel id="exception-date-filter-label">Date Range</InputLabel>
          <Select
            labelId="exception-date-filter-label"
            id="exception-date-filter"
            value={filters.dateRange || ''}
            onChange={(e) => handleFilterChange('dateRange', e.target.value || null)}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            <MenuItem value="7">Last 7 days</MenuItem>
            <MenuItem value="30">Last 30 days</MenuItem>
          </Select>
        </FormControl>

        {/* Client Filter - Disabled due to missing clientId in API response */}
        <FormControl className={classes.formControl} size="small" disabled>
          <InputLabel id="exception-client-filter-label">Client</InputLabel>
          <Select
            labelId="exception-client-filter-label"
            id="exception-client-filter"
            value={filters.clientId || ''}
            onChange={(e) => handleFilterChange('clientId', e.target.value || null)}
          >
            <MenuItem value="">
              <em>All Clients (Disabled)</em>
            </MenuItem>
            {clients.map((client) => (
              <MenuItem key={client.clientId} value={client.clientId}>
                {client.clientName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Clear Filters Button */}
        <Button
          variant="outlined"
          size="small"
          onClick={handleClearFilters}
          disabled={isLoading}
        >
          Clear Filters
        </Button>
      </Box>

      {/* Grid */}
      {exceptions.length === 0 ? (
        <Paper elevation={0} className={classes.tableContainer}>
          <Typography className={classes.noExceptions} color="textSecondary">
            No exceptions found
          </Typography>
          <Typography className={classes.noExceptions} color="textSecondary" variant="body2">
            There are no exception requests on your files
          </Typography>
        </Paper>
      ) : filteredExceptions.length === 0 ? (
        <Paper elevation={0} className={classes.tableContainer}>
          <Typography className={classes.noExceptions} color="textSecondary">
            No exceptions match the current filters
          </Typography>
        </Paper>
      ) : (
        <Box className={classes.tableContainer}>
          <DashboardDataTable
            columns={columns}
            data={filteredExceptions}
            initialSorting={[{ id: 'createdDate', desc: true }]}
          />
        </Box>
      )}
    </>
  );
};

ExceptionCommentsGrid.propTypes = {
  exceptions: PropTypes.arrayOf(PropTypes.shape({
    commentId: PropTypes.number.isRequired,
    fileId: PropTypes.number.isRequired,
    authId: PropTypes.string.isRequired,
    eeName: PropTypes.string.isRequired,
    clientName: PropTypes.string.isRequired,
    exceptionAmount: PropTypes.number,
    exceptionDescription: PropTypes.string.isRequired,
    createdDate: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    createdByName: PropTypes.string.isRequired,
  })).isRequired,
  isLoading: PropTypes.bool,
  clients: PropTypes.arrayOf(
    PropTypes.shape({
      clientId: PropTypes.number,
      clientName: PropTypes.string,
    }),
  ),
  showExpenseAnalyst: PropTypes.bool,
};

ExceptionCommentsGrid.defaultProps = {
  isLoading: false,
  clients: [],
  showExpenseAnalyst: false,
};

export default ExceptionCommentsGrid;
